import type {
  GraphFrameCommitResponse,
  GraphFrameExportFormat,
  GraphFrameExportResponse,
  GraphFrameImportResponse,
  GraphVisualizationFrame
} from "@salesko/contracts";
import { useMutation } from "@tanstack/react-query";
import { type ChangeEvent, useEffect, useRef, useState } from "react";
import {
  createGraphFrameShareLink,
  exportGraphFrame,
  importJsonGraphFrame,
  importObsidianGraphFrame
} from "../../lib/api";
import type { Dictionary } from "../../i18n";
import { downloadJsonArtifact } from "../lib/download";
import {
  jsonGraphFrameImportRequestFromFile,
  obsidianGraphFrameImportRequestFromFiles
} from "../lib/graphFrameFileIo";
import type { FrameExportVariables, FrameImportVariables, FrameIoState } from "../types";

type UseGraphFrameIoOptions = {
  copy: Dictionary["frameIo"];
  frameRecordId: string | null;
  onImportedFrame: (frame: GraphVisualizationFrame, commit: GraphFrameCommitResponse) => void;
  onResetChangeSetState: () => void;
  sessionId: string;
};

export function useGraphFrameIo({
  copy,
  frameRecordId,
  onImportedFrame,
  onResetChangeSetState,
  sessionId
}: UseGraphFrameIoOptions) {
  const jsonFileInputRef = useRef<HTMLInputElement | null>(null);
  const obsidianFileInputRef = useRef<HTMLInputElement | null>(null);
  const [frameIoState, setFrameIoState] = useState<FrameIoState>({ status: "idle" });

  const frameExportMutation = useMutation<GraphFrameExportResponse, Error, FrameExportVariables>({
    mutationFn: ({ format, frameRecordId: exportFrameRecordId }) => exportGraphFrame(exportFrameRecordId, format),
    onMutate: ({ format }) => {
      setFrameIoState({
        status: "exporting",
        format
      });
    },
    onSuccess: (response, variables) => {
      downloadJsonArtifact(response.filename, response.artifact);
      setFrameIoState({
        status: "exported",
        filename: response.filename,
        format: variables.format
      });
    },
    onError: (error, variables) => {
      setFrameIoState({
        status: "error",
        format: variables.format,
        message: error.message || copy.errors.exportFailed
      });
    }
  });

  const frameShareMutation = useMutation({
    mutationFn: (shareFrameRecordId: string) => createGraphFrameShareLink(shareFrameRecordId),
    onMutate: () => {
      setFrameIoState({ status: "sharing" });
    },
    onSuccess: async (response) => {
      const copied = await copyShareUrl(response.shareUrl);
      setFrameIoState({
        status: "shared",
        copied,
        expiresAt: response.expiresAt,
        shareUrl: response.shareUrl
      });
    },
    onError: (error) => {
      setFrameIoState({
        status: "error",
        message: error instanceof Error ? error.message : copy.errors.shareLinkFailed
      });
    }
  });

  const frameImportMutation = useMutation<GraphFrameImportResponse, Error, FrameImportVariables>({
    mutationFn: (variables) =>
      variables.format === "json"
        ? importJsonGraphFrame(variables.request)
        : importObsidianGraphFrame(variables.request),
    onMutate: ({ fileName, format }) => {
      setFrameIoState({
        status: "importing",
        fileName,
        format
      });
    },
    onSuccess: (response, variables) => {
      onImportedFrame(response.frame, response.commit);
      setFrameIoState({
        status: "imported",
        commitId: response.commit.id,
        fileName: variables.fileName,
        format: variables.format,
        importSummary: response.importSummary
      });
      onResetChangeSetState();
    },
    onError: (error, variables) => {
      setFrameIoState({
        status: "error",
        fileName: variables.fileName,
        format: variables.format,
        message: error.message || copy.errors.importFailed
      });
    }
  });

  const handleFrameExportClick = (format: GraphFrameExportFormat) => {
    if (!frameRecordId) {
      setFrameIoState({
        status: "error",
        format,
        message: copy.errors.exportNeedsCommittedFrame
      });
      return;
    }

    frameExportMutation.mutate({
      format,
      frameRecordId
    });
  };

  const handleFrameShareClick = () => {
    if (!frameRecordId) {
      setFrameIoState({
        status: "error",
        message: copy.errors.shareNeedsCommittedFrame
      });
      return;
    }

    frameShareMutation.mutate(frameRecordId);
  };

  const handleJsonImportClick = () => {
    jsonFileInputRef.current?.click();
  };

  const handleObsidianImportClick = () => {
    obsidianFileInputRef.current?.click();
  };

  const handleJsonFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0];
    event.currentTarget.value = "";
    if (!file) {
      return;
    }

    try {
      const request = await jsonGraphFrameImportRequestFromFile(file, {
        messages: copy.fileErrors,
        sessionId,
        submittedAt: new Date().toISOString()
      });
      frameImportMutation.mutate({
        fileName: file.name,
        format: "json",
        request
      });
    } catch (error) {
      setFrameIoState({
        status: "error",
        fileName: file.name,
        format: "json",
        message: error instanceof Error ? error.message : copy.errors.jsonImportFailed
      });
    }
  };

  const handleObsidianFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.currentTarget.files ?? []);
    event.currentTarget.value = "";
    if (files.length === 0) {
      return;
    }

    const fileName = frameImportFileName(files, copy);
    try {
      const request = await obsidianGraphFrameImportRequestFromFiles(files, {
        messages: copy.fileErrors,
        sessionId,
        submittedAt: new Date().toISOString()
      });
      frameImportMutation.mutate({
        fileName,
        format: "obsidian",
        request
      });
    } catch (error) {
      setFrameIoState({
        status: "error",
        fileName,
        format: "obsidian",
        message: error instanceof Error ? error.message : copy.errors.obsidianImportFailed
      });
    }
  };

  useEffect(() => {
    if (!shouldAutoClearFrameIoState(frameIoState.status)) {
      return;
    }

    const timeout = window.setTimeout(() => {
      setFrameIoState({ status: "idle" });
    }, StatusAutoClearMs);
    return () => window.clearTimeout(timeout);
  }, [frameIoState]);

  return {
    dismissFrameIoStatus: () => setFrameIoState({ status: "idle" }),
    frameExportPending: frameExportMutation.isPending,
    frameImportPending: frameImportMutation.isPending,
    frameIoState,
    frameSharePending: frameShareMutation.isPending,
    handleFrameExportClick,
    handleFrameShareClick,
    handleJsonFileChange,
    handleJsonImportClick,
    handleObsidianFileChange,
    handleObsidianImportClick,
    jsonFileInputRef,
    obsidianFileInputRef
  };
}

function frameImportFileName(files: File[], copy: Dictionary["frameIo"]) {
  if (files.length === 1) {
    return files[0]?.name ?? copy.fileFallback;
  }

  return copy.obsidianFilesLabel(files.length);
}

const StatusAutoClearMs = 8_000;

function shouldAutoClearFrameIoState(status: FrameIoState["status"]) {
  return status === "error" || status === "exported" || status === "imported" || status === "shared";
}

async function copyShareUrl(shareUrl: string) {
  if (typeof navigator === "undefined" || !navigator.clipboard?.writeText) {
    return false;
  }

  try {
    await navigator.clipboard.writeText(shareUrl);
    return true;
  } catch {
    return false;
  }
}
